import { useEffect, useMemo, useRef, useState } from 'react';
import type { PaletteEntry } from '../../model/format.ts';
import { spritePixels } from '../../data/customTiles.ts';
import { texImage, useTextureVersion } from '../../terrain/ttShared.ts';
import { Icon } from '../widgets.tsx';
import { applyFlags, crop, getPx, linePoints, type Img } from './img.ts';

const N = 16;

export interface RefTile {
  pe: PaletteEntry;
  flags: number;
}

export const sameRef = (a: RefTile, b: RefTile) => a.pe.tex === b.pe.tex && a.pe.sprite === b.pe.sprite && a.flags === b.flags;

const refKey = (styleId: string) => `ple:te-refs:${styleId}`;

export function loadRefs(styleId: string): RefTile[] {
  try {
    return JSON.parse(localStorage.getItem(refKey(styleId)) ?? '[]');
  } catch {
    return [];
  }
}

export function storeRefs(styleId: string, refs: RefTile[]) {
  localStorage.setItem(refKey(styleId), JSON.stringify(refs));
}

export function refPixels(r: RefTile): Img | null {
  const p = spritePixels(r.pe);
  return p ? applyFlags(p, r.flags) : null;
}

export type RefLayout = 'fit' | 'grid';

interface Props {
  refs: RefTile[];
  /** column width in px */
  width: number;
  layout: RefLayout;
  onLayout: (l: RefLayout) => void;
  onClose: (r: RefTile) => void;
  onFlip: (r: RefTile, flags: number) => void;
  /** a piece dragged out of a reference, with its position in the tile */
  onPiece: (img: Img, x: number, y: number) => void;
  onColor: (c: number, secondary: boolean) => void;
  onStart: (img: Img) => void;
  onAddFromLevel: () => void;
  /** pixel under the pointer, mirrored on the main canvas */
  onHover: (p: [number, number] | null) => void;
  hover: [number, number] | null;
  /** the pencil is the tool: dragging over a tile paints its pixels into the same spots of yours */
  brush: boolean;
  onBrush: (img: Img, pts: [number, number][], phase: 'start' | 'move' | 'end' | 'cancel') => void;
}

/** Tiles opened beside the canvas: drag over one to paste that part at the same spot, tap to pick a colour. */
export function RefColumn(p: Props) {
  // fit: one tile per row as wide as the column; grid: small tiles side by side
  // on phones the column is a strip under the canvas: its width setting doesn't apply
  const w = window.matchMedia('(max-width: 900px)').matches ? 160 : p.width;
  const z = p.layout === 'fit' ? Math.max(6, Math.floor((w - 20) / N)) : 8;
  return (
    <div className="te-refs" style={{ width: p.width }}>
      <div className="te-refs-head">
        <b>Beside</b>
        <span className="te-grow" />
        <button className={`chip small ${p.layout === 'fit' ? 'active' : ''}`} onClick={() => p.onLayout('fit')} title="Tiles as wide as the column (drag its left edge to make them bigger)">Fit</button>
        <button className={`chip small ${p.layout === 'grid' ? 'active' : ''}`} onClick={() => p.onLayout('grid')} title="Small tiles side by side">Grid</button>
      </div>
      <div className={`te-refs-list ${p.layout}`}>
        {p.refs.map((r) => (
          <RefCard key={`${r.pe.tex}|${r.pe.sprite}|${r.flags}`} r={r} z={z} {...p} />
        ))}
      </div>
      <button className="te-ref-add" onClick={p.onAddFromLevel} title="Tap a tile in the level to open it here">
        <Icon name="target" size={16} /> From level
      </button>
      {!p.refs.length && <div className="muted small te-refs-hint">Open tiles here to copy from: tap a sprite on the tileset sheet, a pinned tile, or pick one from the level.</div>}
    </div>
  );
}

function RefCard({ r, z, onClose, onFlip, onPiece, onColor, onStart, onHover, hover, brush, onBrush }: Props & { r: RefTile; z: number }) {
  const painting = useRef<[number, number] | null>(null);
  const tv = useTextureVersion();
  const [, setLoaded] = useState(0);
  const img = useMemo(() => refPixels(r), [r, tv]);
  const ref = useRef<HTMLCanvasElement>(null);
  const [sel, setSel] = useState<{ x0: number; y0: number; x1: number; y1: number; button: number } | null>(null);
  const T = N * z;

  useEffect(() => {
    if (!img) texImage(r.pe.tex, () => setLoaded((n) => n + 1));
  }, [img, r]);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(T * dpr);
    c.height = Math.round(T * dpr);
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (let y = 0; y < N; y++)
      for (let x = 0; x < N; x++) {
        const col = img ? getPx(img, x, y) : 0;
        g.fillStyle = (x + y) & 1 ? '#2b2b36' : '#33333f';
        g.fillRect(x * z, y * z, z, z);
        if (col) {
          g.fillStyle = `rgba(${col >>> 24},${(col >>> 16) & 255},${(col >>> 8) & 255},${(col & 255) / 255})`;
          g.fillRect(x * z, y * z, z, z);
        }
      }
    if (z >= 8) {
      g.strokeStyle = 'rgba(255,255,255,0.06)';
      g.beginPath();
      for (let i = 1; i < N; i++) {
        g.moveTo(i * z + 0.5, 0);
        g.lineTo(i * z + 0.5, T);
        g.moveTo(0, i * z + 0.5);
        g.lineTo(T, i * z + 0.5);
      }
      g.stroke();
    }
    if (sel) {
      const x = Math.min(sel.x0, sel.x1), y = Math.min(sel.y0, sel.y1);
      g.strokeStyle = '#4fc3ff';
      g.lineWidth = 2;
      g.setLineDash([4, 3]);
      g.strokeRect(x * z + 1, y * z + 1, (Math.abs(sel.x1 - sel.x0) + 1) * z - 2, (Math.abs(sel.y1 - sel.y0) + 1) * z - 2);
      g.setLineDash([]);
    } else if (hover && hover[0] >= 0 && hover[1] >= 0 && hover[0] < N && hover[1] < N) {
      g.strokeStyle = 'rgba(255,255,255,0.8)';
      g.lineWidth = 1;
      g.strokeRect(hover[0] * z + 0.5, hover[1] * z + 0.5, z - 1, z - 1);
    }
  });

  const at = (e: React.PointerEvent): [number, number] => {
    const b = ref.current!.getBoundingClientRect();
    return [Math.max(0, Math.min(N - 1, Math.floor((e.clientX - b.left) / z))), Math.max(0, Math.min(N - 1, Math.floor((e.clientY - b.top) / z)))];
  };
  const finish = () => {
    if (!sel || !img) return setSel(null);
    const x = Math.min(sel.x0, sel.x1), y = Math.min(sel.y0, sel.y1);
    const w = Math.abs(sel.x1 - sel.x0) + 1, h = Math.abs(sel.y1 - sel.y0) + 1;
    setSel(null);
    if (w * h === 1) onColor(getPx(img, x, y), sel.button === 2);
    else onPiece(crop(img, x, y, w, h), x, y);
  };

  return (
    <div className="te-ref" style={{ width: T + 2 }}>
      <canvas
        ref={ref}
        title={brush ? `${r.pe.sprite}: paint over it to copy those pixels into the same spots of your tile (other tools: drag to copy a part, tap to pick a colour)` : `${r.pe.sprite}: drag to copy part of it into your tile, tap to pick a colour`}
        style={{ width: T, height: T, cursor: brush ? 'cell' : undefined }}
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={(e) => {
          e.preventDefault();
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          const [x, y] = at(e);
          if (brush && e.button === 0 && img) {
            painting.current = [x, y];
            onBrush(img, [[x, y]], 'start');
            return;
          }
          setSel({ x0: x, y0: y, x1: x, y1: y, button: e.button });
        }}
        onPointerMove={(e) => {
          const [x, y] = at(e);
          const last = painting.current;
          if (last && img && (last[0] !== x || last[1] !== y)) {
            onBrush(img, linePoints(last[0], last[1], x, y).slice(1), 'move');
            painting.current = [x, y];
          }
          if (sel && (sel.x1 !== x || sel.y1 !== y)) setSel({ ...sel, x1: x, y1: y });
          if (!hover || hover[0] !== x || hover[1] !== y) onHover([x, y]);
        }}
        onPointerUp={() => {
          if (painting.current) {
            painting.current = null;
            if (img) onBrush(img, [], 'end');
            return;
          }
          finish();
        }}
        onPointerCancel={() => {
          if (painting.current && img) onBrush(img, [], 'cancel');
          painting.current = null;
          setSel(null);
        }}
        onPointerLeave={() => !sel && !painting.current && onHover(null)}
      />
      <div className="te-ref-bar">
        <button className="te-ref-btn" title="Paste the whole tile as a floating piece" onClick={() => img && onPiece(img, 0, 0)}><Icon name="paste" size={14} /></button>
        <button className="te-ref-btn" title="Replace your tile with this one (undo with Ctrl+Z)" onClick={() => img && onStart(img)}>Use</button>
        <button className="te-ref-btn" title="Flip left / right" onClick={() => onFlip(r, r.flags ^ 1)}><Icon name="flipx" size={14} /></button>
        <button className="te-ref-btn" title="Flip up / down" onClick={() => onFlip(r, r.flags ^ 2)}><Icon name="flipy" size={14} /></button>
        <button className="te-ref-btn" title="Close" onClick={() => onClose(r)}><Icon name="close" size={14} /></button>
      </div>
    </div>
  );
}
